import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { SessionService } from '@app/security/session.service';
import { ProgressTabsComponent } from '../../../shared/progress-tabs/progress-tabs.component';
import { BreadcrumbsComponent } from '../../../shared/breadcrumbs/breadcrumbs.component';
import { MessageDialogComponent } from '../../../shared/message-dialog/message-dialog.component';

@Component({
    templateUrl: './client-config-container.component.html',
    styleUrls: ['./client-config-container.component.scss'],
    standalone: true,
    imports: [
        ProgressTabsComponent,
        BreadcrumbsComponent,
        RouterOutlet,
        MessageDialogComponent,
    ],
})
export class ClientConfigContainerComponent implements OnInit, OnDestroy {
  routeSub$: Subscription;
  hideCrumbs: boolean = false;
  subnavLinks: { text: string; path: string }[];
  statusTabs: object[] = [
    { id: 'client-info', text: 'Client Information' },
    { id: 'product-group', text: 'Product Group Information' },
  ];

  constructor(
    private activatedRoute: ActivatedRoute,
    private sessionService: SessionService,
    private router: Router,
  ) {}

  ngOnInit() {
    this.setSubnavLinks();
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.setSubnavLinks();
      });
  }

  ngOnDestroy() {
    this.routeSub$.unsubscribe();
  }

  isClientEdit(segment2): boolean {
    return segment2?.path === 'edit';
  }

  isPgEdit(segment2, pgId): boolean {
    return segment2?.path === 'product-group' && pgId != null;
  }

  setSubnavLinks(): void {
    const routeInfo = this.activatedRoute.snapshot.children[0];
    const cId = routeInfo.url[0];
    const segment2 = routeInfo.url[1]; // 'edit' or 'product-group'  (not sure what else to call this?)
    const pgId = routeInfo.url[2];

    this.showHideCrumbs();

    /* Product Group */
    if (this.isPgEdit(segment2, pgId)) {
      this.subnavLinks = [
        { text: 'Basics', path: `/client/${cId}/product-group/${pgId}` },
        { text: 'Legal', path: `/client/${cId}/product-group/${pgId}/legal` },
        {
          text: 'Onboarding',
          path: `/client/${cId}/product-group/${pgId}/onboarding`,
        },
      ];
      this.subnavLinks.push({
        text: 'Spanish Translations',
        path: `/client/${cId}/product-group/${pgId}/translations`,
      });

      /* Client Information */
    } else if (this.isClientEdit(segment2)) {
      this.subnavLinks = [
        { text: 'Basics', path: `/client/${cId}/edit` },
        { text: 'Settings', path: `/client/${cId}/edit/branding-settings` },
        { text: 'Validation', path: `/client/${cId}/edit/validation` },
        { text: 'Inputs', path: `/client/${cId}/edit/inputs` },
        { text: 'Onboarding', path: `/client/${cId}/edit/onboarding` }
      ];
      
      this.sessionService.getClient(`${cId}`).subscribe(client => {
        if (client.powerups_enabled === true) {
          this.subnavLinks.push({ text: 'Power-Ups', path: `/client/${cId}/edit/power-ups-config` });
        }
      });
      /* Neither */
    } else {
      this.subnavLinks = [];
    }
  }

  tabClicked(tabId) {
    if (tabId === 'client-info') {
      this.goToClient();
    } else {
      this.goToPGList();
    }
  }

  goToClient(): void {
    const routeInfo = this.activatedRoute.snapshot.children[0];
    if (routeInfo.url[1]?.path !== 'edit') {
      this.router.navigateByUrl(
        '/client/' + routeInfo.params.clientId + '/edit',
      );
    }
  }

  activeTabId(): string {
    return this.activatedRoute.snapshot.children[0].data.step;
  }

  isComplete(param: string): boolean {
    return this.activatedRoute.snapshot.children[0].data.step === param;
  }

  goToPGList(): void {
    const routeInfo = this.activatedRoute.snapshot.children[0];

    if (routeInfo.url[1]?.path === 'edit') {
      this.router.navigateByUrl(
        '/client/' + routeInfo.params.clientId + '/product-group',
      );
    }
  }

  private showHideCrumbs(): void {
    const key = '/client';
    this.hideCrumbs = this.router.url === key;
    this.routeSub$ = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.hideCrumbs = event.urlAfterRedirects === key;
      }
    });
  }
}
